import React, { useState, useEffect } from 'react';
import { Menu, X, Lock, Calendar, ChevronDown, Home as HomeIcon, Compass, Building2, Users as UsersIcon, Sparkles, User, LogOut } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AppointmentModal } from './AppointmentModal';
import { InvestorAuthModal } from './InvestorAuthModal';
import { AddPropertyModal } from './AddPropertyModal';
import { QuickLandLeadModal } from './QuickLandLeadModal';
import { useInvestorAuth } from '../contexts/InvestorAuthContext';

export const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { investor, logout: investorLogout } = useInvestorAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showAppointment, setShowAppointment] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showListProperty, setShowListProperty] = useState(false);
  const [showQuickLand, setShowQuickLand] = useState(false);
  const [customerMenuOpen, setCustomerMenuOpen] = useState(false);
  // Which collapsible mobile groups are open (Properties is open by default so users see land types immediately)
  const [openGroups, setOpenGroups] = useState({ properties: true, explore: false, connect: false });
  const toggleGroup = (key) => setOpenGroups((g) => ({ ...g, [key]: !g[key] }));

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Global event so the ticker (and any other component) can open the modal.
  useEffect(() => {
    const open = () => setShowAppointment(true);
    window.addEventListener('openAppointmentModal', open);
    return () => window.removeEventListener('openAppointmentModal', open);
  }, []);

  // Global event so any CTA on the site can open the List-your-property form.
  useEffect(() => {
    const open = () => setShowListProperty(true);
    window.addEventListener('openListPropertyModal', open);
    return () => window.removeEventListener('openListPropertyModal', open);
  }, []);

  // Lock body scroll while mobile drawer is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [isMobileMenuOpen]);

  const scrollToSection = (sectionId) => {
    // When we're on a sub-page (e.g. /track, /land-fill, /investors), the
    // section IDs from the home page don't exist in the DOM — clicking would
    // silently do nothing. Route to home with a hash so the target section
    // is available on mount, then App.js scrolls to it.
    if (location.pathname !== '/') {
      navigate(`/#${sectionId}`);
      setIsMobileMenuOpen(false);
      return;
    }
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const navItems = [
    { label: 'Home', id: 'home' },
    { label: 'About', id: 'about' },
    { label: 'Founders', id: 'founders' },
    { label: 'Services', id: 'services' },
    {
      label: 'Properties',
      id: 'projects',
      dropdown: [
        { label: 'All Properties', type: null },
        { label: 'Residential', type: 'Residential' },
        { label: 'Agricultural', type: 'Agricultural' },
        { label: 'Commercial', type: 'Commercial' },
        { label: 'Flat Rent', type: 'Flat Rent' },
        { label: 'Flat Sell', type: 'Flat Sell' },
        { label: 'House Rent', type: 'House Rent' },
        { label: 'House Sell', type: 'House Sell' },
      ],
    },
    { label: 'News', id: 'news' },
    { label: 'Career', id: 'career' },
    { label: 'Join as\nAgent', id: 'join-agent', stacked: true },
    { label: 'List Property', id: 'add-property', highlight: true },
    { label: 'Architect', id: 'ai-architect-page', route: '/ai-architect', highlight: true },
    { label: 'Investors', id: 'land-investors-page', route: '/land-investors', highlight: true },
    { label: 'Track Order', id: 'track-page', route: '/track' },
    { label: 'Testimonials', id: 'testimonials' },
    { label: 'Contact', id: 'contact' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-md' : 'bg-white/90 backdrop-blur-sm'
      }`}
    >
      <div className="w-full px-3 lg:px-6 xl:px-8">
        <div className="flex items-center justify-between gap-3 h-20">
          {/* Logo */}
          <div
            className="flex items-center cursor-pointer flex-shrink-0"
            onClick={() => scrollToSection('home')}
            data-testid="header-logo"
          >
            <img
              src={`${process.env.PUBLIC_URL || ''}/acremitra-logo.png`}
              alt="Acre Mitra — Your Trusted Real Estate Partner"
              className="h-20 w-auto object-contain"
              onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextElementSibling?.classList.remove('hidden'); }}
              data-testid="header-logo-img"
            />
            {/* Text fallback shown only if the image 404s */}
            <div className="hidden flex-col leading-none">
              <span className="text-emerald-700 font-extrabold text-xl tracking-tight">Acre Mitra</span>
              <span className="text-[9px] uppercase tracking-[0.22em] text-amber-500 font-bold mt-1">Your Trusted Real Estate Partner</span>
            </div>
          </div>

          {/* Desktop Navigation — grouped hover dropdowns, mirrors the mobile drawer structure */}
          <nav className="hidden lg:flex items-center gap-1">
            {/* Home */}
            <button
              onClick={() => scrollToSection('home')}
              className="am-nav-link"
              data-testid="nav-home"
            >
              Home
            </button>

            {/* Properties dropdown (with sub-types) */}
            <DesktopDropdown label="Properties" testId="nav-properties" onClickLabel={() => scrollToSection('projects')}>
              {[
                { label: 'All Properties', type: null },
                { label: 'Residential', type: 'Residential' },
                { label: 'Agricultural', type: 'Agricultural' },
                { label: 'Commercial', type: 'Commercial' },
                { label: 'Flat Rent', type: 'Flat Rent' },
                { label: 'Flat Sell', type: 'Flat Sell' },
                { label: 'House Rent', type: 'House Rent' },
                { label: 'House Sell', type: 'House Sell' },
              ].map((sub) => (
                <button
                  key={sub.label}
                  onClick={() => {
                    scrollToSection('projects');
                    setTimeout(() => {
                      window.dispatchEvent(new CustomEvent('filterPropertyType', { detail: { type: sub.type || 'All' } }));
                    }, 300);
                  }}
                  className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
                >
                  {sub.label}
                </button>
              ))}
            </DesktopDropdown>

            {/* Projects dropdown — Upcoming + fractional Invest in Property */}
            <DesktopDropdown label="Projects" testId="nav-projects-menu">
              <button
                onClick={() => scrollToSection('projects')}
                className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
                data-testid="nav-upcoming-projects"
              >
                Upcoming Projects
              </button>
              <button
                onClick={() => { navigate('/invest'); setIsMobileMenuOpen(false); }}
                className="w-full text-left px-4 py-2.5 text-sm text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800 font-bold transition-colors inline-flex items-center gap-2"
                data-testid="nav-invest-in-property"
              >
                Invest in Property
                <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded">New</span>
              </button>
            </DesktopDropdown>

            {/* Explore dropdown — About, Founders, Services, News, Testimonials */}
            <DesktopDropdown label="Explore" testId="nav-explore">
              {[
                { label: 'About',              id: 'about' },
                { label: 'Founders',           id: 'founders' },
                { label: 'Services',           id: 'services' },
                { label: 'News',               id: 'news' },
                { label: 'Testimonials',       id: 'testimonials' },
                { label: 'Site Intel Scanner', id: '__siteintel', badge: 'NEW' },
              ].map((sub) => (
                <button
                  key={sub.id}
                  onClick={() => (sub.id === '__siteintel' ? navigate('/site-intel') : scrollToSection(sub.id))}
                  className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors flex items-center gap-2"
                  data-testid={`nav-${sub.id.replace('__', '')}`}
                >
                  <span className="flex-1">{sub.label}</span>
                  {sub.badge && <span className="text-[9px] font-black uppercase tracking-widest rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 px-1.5 py-0.5">{sub.badge}</span>}
                </button>
              ))}
            </DesktopDropdown>

            {/* Join & Contact dropdown */}
            <DesktopDropdown label="Join & Contact" testId="nav-connect">
              {[
                { label: 'Career',        id: 'career' },
                { label: 'Join as Agent', id: 'join-agent' },
                { label: 'Contact',       id: 'contact' },
                { label: 'Track Order',   id: '__track' },
                { label: 'Customer Care', id: '__support' },
              ].map((sub) => (
                <button
                  key={sub.id}
                  onClick={() => (sub.id === '__track' ? navigate('/track') : sub.id === '__support' ? navigate('/support') : scrollToSection(sub.id))}
                  className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
                  data-testid={`nav-${sub.id}`}
                >
                  {sub.label}
                </button>
              ))}
            </DesktopDropdown>

            {/* Divider */}
            <span className="mx-1 h-6 w-px bg-slate-200" aria-hidden="true" />

            {/* Highlighted amber CTAs — always visible */}
            <button
              onClick={() => setShowQuickLand(true)}
              className="am-nav-link !text-amber-700 hover:!text-amber-800"
              data-testid="header-list-property-btn"
            >
              List Property
            </button>
            {/* Architect dropdown — nests the Land-Fill estimator under Architect */}
            <div className="relative group">
              <button
                onClick={() => navigate('/ai-architect')}
                className="am-nav-link !text-amber-700 hover:!text-amber-800"
                data-testid="nav-ai-architect-page"
              >
                Architect
                <ChevronDown size={12} className="am-nav-chevron text-amber-500" />
              </button>
              <div className="absolute left-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="w-56 bg-white rounded-xl shadow-xl border border-slate-200 py-1 overflow-hidden">
                  <button
                    onClick={() => navigate('/ai-architect')}
                    className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors flex flex-col"
                    data-testid="nav-architect-sub"
                  >
                    <span className="font-semibold">Architect</span>
                    <span className="text-[10px] text-slate-500">Blueprint + cost estimator</span>
                  </button>
                  <button
                    onClick={() => navigate('/land-fill')}
                    className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors flex flex-col border-t border-slate-100"
                    data-testid="nav-land-fill-page"
                  >
                    <span className="font-semibold">Land Fill</span>
                    <span className="text-[10px] text-slate-500">Plot fill cost estimator</span>
                  </button>
                </div>
              </div>
            </div>
            <button
              onClick={() => navigate('/land-investors')}
              className="am-nav-link !text-amber-700 hover:!text-amber-800"
              data-testid="nav-land-investors-page"
            >
              Investors
            </button>
          </nav>

          {/* CTA Buttons - Desktop (small colored pills in Acre Mitra brand colors) */}
          <div className="hidden lg:flex items-center gap-2">
            <button
              onClick={() => setShowAppointment(true)}
              className="inline-flex items-center gap-1.5 text-[11px] font-bold text-white bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 rounded-full px-3.5 py-1.5 shadow-sm hover:shadow-md transition-all"
              data-testid="header-appointment-btn"
              title="Book an appointment with our team"
            >
              <Calendar size={12} />
              Appointment
            </button>

            {/* Customer account — Sign In pill when logged out, avatar dropdown when logged in */}
            {investor ? (
              <div className="relative">
                <button
                  onClick={() => setCustomerMenuOpen((v) => !v)}
                  onBlur={() => setTimeout(() => setCustomerMenuOpen(false), 180)}
                  className="inline-flex items-center gap-1.5 text-[11px] font-bold text-emerald-800 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-full pl-1.5 pr-2.5 py-1 shadow-sm hover:shadow-md transition-all"
                  data-testid="header-customer-menu"
                >
                  <span className="w-5 h-5 rounded-full bg-gradient-to-br from-emerald-600 to-emerald-500 text-white grid place-items-center text-[10px] font-black">
                    {(investor.name || 'A').trim().charAt(0).toUpperCase()}
                  </span>
                  {(investor.name || 'Account').split(' ')[0]}
                  <ChevronDown size={11} />
                </button>
                {customerMenuOpen && (
                  <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-50">
                    <div className="px-3 py-2 bg-slate-50 border-b border-slate-100">
                      <div className="text-[11px] text-slate-500">Signed in as</div>
                      <div className="text-xs font-bold text-slate-800 truncate">{investor.email}</div>
                    </div>
                    <button
                      onMouseDown={() => { navigate('/my-account'); setCustomerMenuOpen(false); }}
                      className="w-full text-left px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-emerald-50 hover:text-emerald-700"
                      data-testid="header-my-account"
                    >
                      My Account
                    </button>
                    <button
                      onMouseDown={() => { navigate('/my-account?tab=orders'); setCustomerMenuOpen(false); }}
                      className="w-full text-left px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-emerald-50 hover:text-emerald-700"
                      data-testid="header-my-orders"
                    >
                      My Orders
                    </button>
                    <button
                      onMouseDown={() => { navigate('/my-account?tab=track'); setCustomerMenuOpen(false); }}
                      className="w-full text-left px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-emerald-50 hover:text-emerald-700"
                      data-testid="header-track-order"
                    >
                      Track My Order
                    </button>
                    <button
                      onMouseDown={() => { navigate('/my-account?tab=invoices'); setCustomerMenuOpen(false); }}
                      className="w-full text-left px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-emerald-50 hover:text-emerald-700"
                      data-testid="header-my-invoices"
                    >
                      Invoice Details
                    </button>
                    <button
                      onMouseDown={() => { investorLogout(); setCustomerMenuOpen(false); }}
                      className="w-full text-left px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50 border-t border-slate-100 inline-flex items-center gap-1.5"
                      data-testid="header-logout"
                    >
                      <LogOut size={11} /> Log out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={() => setShowLogin(true)}
                className="inline-flex items-center gap-1.5 whitespace-nowrap text-[11px] font-bold text-emerald-700 hover:text-emerald-800 bg-white hover:bg-emerald-50 border border-emerald-200 rounded-full px-3.5 py-1.5 shadow-sm hover:shadow-md transition-all"
                data-testid="header-signin-btn"
                title="Sign in to your Acre Mitra account"
              >
                <User size={12} />
                Sign In
              </button>
            )}

            <button
              onClick={() => navigate('/admin/login')}
              className="inline-flex items-center gap-1.5 text-[11px] font-bold text-white bg-gradient-to-r from-amber-500 to-amber-400 hover:from-amber-600 hover:to-amber-500 rounded-full px-3.5 py-1.5 shadow-sm hover:shadow-md transition-all"
              data-testid="admin-login-btn"
              title="Admin panel"
            >
              <Lock size={12} />
              Admin
            </button>
          </div>

          {/* Mobile Menu Button (mobile + tablet, < 1024px) — sign-in lives inside the drawer to keep the top-bar clean */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-slate-700 hover:text-emerald-700 transition-colors"
            data-testid="mobile-menu-toggle"
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile / Tablet Slide-in Drawer — full viewport height, internally scrollable so ALL desktop options are reachable */}
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <button
              type="button"
              aria-label="Close menu"
              className="lg:hidden fixed left-0 right-0 top-20 h-[calc(100vh-5rem)] bg-slate-900/40 backdrop-blur-sm z-40"
              onClick={() => setIsMobileMenuOpen(false)}
              data-testid="mobile-menu-backdrop"
            />
            {/* Drawer panel */}
            <div
              className="lg:hidden fixed left-0 right-0 top-20 h-[calc(100vh-5rem)] bg-white z-40 overflow-y-auto shadow-2xl"
              data-testid="mobile-menu-drawer"
            >
              <nav className="flex flex-col py-3 pb-28">
                {/* 1. Home — always visible, single tap */}
                <button
                  onClick={() => scrollToSection('home')}
                  className="mx-4 px-4 py-3 text-left font-semibold rounded-lg text-slate-800 hover:text-emerald-700 hover:bg-emerald-50 text-[15px] flex items-center gap-2.5"
                  data-testid="mobile-nav-home"
                >
                  <HomeIcon size={16} className="text-emerald-700" />
                  Home
                </button>

                {/* 2. Highlighted CTAs — always visible (biggest conversion levers) */}
                <div className="mx-4 mt-2 mb-1 grid grid-cols-1 gap-1.5" data-testid="mobile-highlights">
                  {/* Architect group — Land Fill nested beneath it (matches desktop dropdown) */}
                  <div className="rounded-lg bg-amber-50 border border-amber-200/60 overflow-hidden">
                    <button
                      onClick={() => (navigate('/ai-architect'), setIsMobileMenuOpen(false))}
                      className="w-full px-4 py-3 text-left font-semibold text-amber-700 hover:bg-amber-100 text-[15px] flex items-center gap-2.5"
                      data-testid="mobile-nav-ai-architect-page"
                    >
                      Architect
                      <span className="ml-auto text-[10px] font-bold uppercase tracking-wider text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full">New</span>
                    </button>
                    <button
                      onClick={() => (navigate('/land-fill'), setIsMobileMenuOpen(false))}
                      className="w-full px-4 py-2.5 text-left font-medium text-amber-800 hover:bg-amber-100 text-sm flex items-center gap-2.5 border-t border-amber-200/60 pl-10"
                      data-testid="mobile-nav-land-fill-page"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                      Land Fill
                      <span className="text-[10px] text-amber-600/80 ml-1">· Plot fill estimator</span>
                    </button>
                  </div>
                  <button
                    onClick={() => (navigate('/land-investors'), setIsMobileMenuOpen(false))}
                    className="px-4 py-3 text-left font-semibold rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 text-[15px] flex items-center gap-2.5 border border-amber-200/60"
                    data-testid="mobile-nav-land-investors-page"
                  >
                    <UsersIcon size={16} />
                    Investors
                  </button>
                  <button
                    onClick={() => { setShowQuickLand(true); setIsMobileMenuOpen(false); }}
                    className="px-4 py-3 text-left font-semibold rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 text-[15px] flex items-center gap-2.5 border border-amber-200/60"
                    data-testid="mobile-nav-add-property"
                  >
                    <Building2 size={16} />
                    List Property
                  </button>
                </div>

                {/* 3. Properties (collapsible with sub-types) */}
                <div className="mx-4 mt-3">
                  <button
                    onClick={() => toggleGroup('properties')}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-slate-800 hover:bg-slate-100 text-[15px] font-semibold"
                    aria-expanded={openGroups.properties}
                    data-testid="mobile-group-properties"
                  >
                    <span className="flex items-center gap-2.5">
                      <Building2 size={16} className="text-emerald-700" />
                      Properties
                    </span>
                    <ChevronDown size={16} className={`text-slate-500 transition-transform duration-200 ${openGroups.properties ? 'rotate-180' : ''}`} />
                  </button>
                  {openGroups.properties && (
                    <div className="mt-1 mb-1 pl-4 border-l-2 border-emerald-100 ml-4 space-y-0.5" data-testid="mobile-group-properties-content">
                      {[
                        { label: 'All Properties', type: null },
                        { label: 'Residential', type: 'Residential' },
                        { label: 'Agricultural', type: 'Agricultural' },
                        { label: 'Commercial', type: 'Commercial' },
                        { label: 'Flat Rent', type: 'Flat Rent' },
                        { label: 'Flat Sell', type: 'Flat Sell' },
                        { label: 'House Rent', type: 'House Rent' },
                        { label: 'House Sell', type: 'House Sell' },
                      ].map((sub) => (
                        <button
                          key={sub.label}
                          onClick={() => {
                            scrollToSection('projects');
                            setTimeout(() => {
                              window.dispatchEvent(new CustomEvent('filterPropertyType', { detail: { type: sub.type || 'All' } }));
                            }, 300);
                          }}
                          className="w-full text-left px-3 py-2 text-sm text-slate-600 hover:text-emerald-700 hover:bg-emerald-50 rounded"
                        >
                          {sub.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* 3b. Projects — Upcoming + Invest in Property */}
                <div className="mx-4 mt-2 space-y-1">
                  <button
                    onClick={() => { scrollToSection('projects'); }}
                    className="w-full text-left px-4 py-3 rounded-lg text-slate-800 hover:bg-slate-100 text-[15px] font-semibold"
                    data-testid="mnav-upcoming-projects"
                  >
                    Upcoming Projects
                  </button>
                  <button
                    onClick={() => { navigate('/invest'); setIsMobileMenuOpen(false); }}
                    className="w-full flex items-center gap-2 text-left px-4 py-3 rounded-lg text-emerald-700 bg-emerald-50 hover:bg-emerald-100 text-[15px] font-bold"
                    data-testid="mnav-invest-in-property"
                  >
                    Invest in Property
                    <span className="ml-auto text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded">New</span>
                  </button>
                </div>

                {/* 4. Explore (collapsible) — About, Founders, Services, News, Testimonials */}
                <div className="mx-4 mt-2">
                  <button
                    onClick={() => toggleGroup('explore')}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-slate-800 hover:bg-slate-100 text-[15px] font-semibold"
                    aria-expanded={openGroups.explore}
                    data-testid="mobile-group-explore"
                  >
                    <span className="flex items-center gap-2.5">
                      <Compass size={16} className="text-emerald-700" />
                      Explore
                    </span>
                    <ChevronDown size={16} className={`text-slate-500 transition-transform duration-200 ${openGroups.explore ? 'rotate-180' : ''}`} />
                  </button>
                  {openGroups.explore && (
                    <div className="mt-1 mb-1 pl-4 border-l-2 border-emerald-100 ml-4 space-y-0.5" data-testid="mobile-group-explore-content">
                      {[
                        { label: 'About',              id: 'about' },
                        { label: 'Founders',           id: 'founders' },
                        { label: 'Services',           id: 'services' },
                        { label: 'News',               id: 'news' },
                        { label: 'Testimonials',       id: 'testimonials' },
                        { label: 'Site Intel Scanner', id: '__siteintel', badge: 'NEW' },
                      ].map((sub) => (
                        <button
                          key={sub.id}
                          onClick={() => (sub.id === '__siteintel' ? navigate('/site-intel') : scrollToSection(sub.id))}
                          className="w-full text-left px-3 py-2 text-sm text-slate-600 hover:text-emerald-700 hover:bg-emerald-50 rounded flex items-center gap-2"
                          data-testid={`mobile-nav-${sub.id.replace('__', '')}`}
                        >
                          <span className="flex-1">{sub.label}</span>
                          {sub.badge && <span className="text-[9px] font-black uppercase tracking-widest rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 px-1.5 py-0.5">{sub.badge}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* 5. Join & Contact (collapsible) — Career, Join as Agent, Contact, Track Order */}
                <div className="mx-4 mt-2">
                  <button
                    onClick={() => toggleGroup('connect')}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-slate-800 hover:bg-slate-100 text-[15px] font-semibold"
                    aria-expanded={openGroups.connect}
                    data-testid="mobile-group-connect"
                  >
                    <span className="flex items-center gap-2.5">
                      <UsersIcon size={16} className="text-emerald-700" />
                      Join &amp; Contact
                    </span>
                    <ChevronDown size={16} className={`text-slate-500 transition-transform duration-200 ${openGroups.connect ? 'rotate-180' : ''}`} />
                  </button>
                  {openGroups.connect && (
                    <div className="mt-1 mb-1 pl-4 border-l-2 border-emerald-100 ml-4 space-y-0.5" data-testid="mobile-group-connect-content">
                      {[
                        { label: 'Career',         id: 'career',      route: null },
                        { label: 'Join as Agent',  id: 'join-agent',  route: null },
                        { label: 'Contact',        id: 'contact',     route: null },
                        { label: 'Track Order',    id: 'track-page',  route: '/track' },
                        { label: 'Customer Care',  id: 'support-page', route: '/support' },
                      ].map((sub) => (
                        <button
                          key={sub.id}
                          onClick={() => sub.route ? (navigate(sub.route), setIsMobileMenuOpen(false)) : scrollToSection(sub.id)}
                          className="w-full text-left px-3 py-2 text-sm text-slate-600 hover:text-emerald-700 hover:bg-emerald-50 rounded"
                          data-testid={`mobile-nav-${sub.id}`}
                        >
                          {sub.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* 6. CTAs at the bottom */}
                <div className="px-4 pt-4 mt-4 border-t border-slate-200 space-y-2">
                  {/* Customer account / Sign In */}
                  {investor ? (
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 overflow-hidden" data-testid="mobile-customer-menu">
                      <div className="px-4 py-3 flex items-center gap-3 border-b border-emerald-200/60">
                        <span className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-600 to-emerald-500 text-white grid place-items-center text-sm font-black">
                          {(investor.name || 'A').trim().charAt(0).toUpperCase()}
                        </span>
                        <div className="min-w-0">
                          <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700">Signed in</div>
                          <div className="text-sm font-bold text-slate-900 truncate">{investor.name || 'My Account'}</div>
                          <div className="text-[11px] text-slate-500 truncate">{investor.email}</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-1 p-2">
                        <button
                          onClick={() => { navigate('/my-account'); setIsMobileMenuOpen(false); }}
                          className="text-left px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-white rounded-md"
                          data-testid="mobile-my-account"
                        >
                          My Account
                        </button>
                        <button
                          onClick={() => { navigate('/my-account?tab=orders'); setIsMobileMenuOpen(false); }}
                          className="text-left px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-white rounded-md"
                          data-testid="mobile-my-orders"
                        >
                          My Orders
                        </button>
                        <button
                          onClick={() => { navigate('/my-account?tab=track'); setIsMobileMenuOpen(false); }}
                          className="text-left px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-white rounded-md"
                          data-testid="mobile-track-order-link"
                        >
                          Track Order
                        </button>
                        <button
                          onClick={() => { navigate('/my-account?tab=invoices'); setIsMobileMenuOpen(false); }}
                          className="text-left px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-white rounded-md"
                          data-testid="mobile-my-invoices"
                        >
                          Invoices
                        </button>
                      </div>
                      <button
                        onClick={() => { investorLogout(); setIsMobileMenuOpen(false); }}
                        className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-bold text-red-600 hover:bg-red-50 border-t border-emerald-200/60"
                        data-testid="mobile-logout"
                      >
                        <LogOut size={13} /> Log out
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => { setIsMobileMenuOpen(false); setShowLogin(true); }}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full font-bold text-emerald-700 bg-white border-2 border-emerald-200 hover:bg-emerald-50 hover:border-emerald-300 transition-all"
                      data-testid="mobile-signin-btn"
                    >
                      <User size={16} />
                      Sign In / Create Account
                    </button>
                  )}

                  <button
                    onClick={() => {
                      setShowAppointment(true);
                      setIsMobileMenuOpen(false);
                    }}
                    className="btn-am-accent w-full"
                    data-testid="mobile-appointment-btn"
                  >
                    <Calendar size={16} />
                    Book Appointment
                  </button>
                  <button
                    onClick={() => { navigate('/admin/login'); setIsMobileMenuOpen(false); }}
                    className="btn-am-secondary w-full"
                    data-testid="mobile-admin-btn"
                  >
                    <Lock size={16} />
                    Admin Login
                  </button>
                </div>
              </nav>
            </div>
          </>
        )}
      </div>
      <AppointmentModal isOpen={showAppointment} onClose={() => setShowAppointment(false)} />
      <InvestorAuthModal
        open={showLogin}
        onClose={() => setShowLogin(false)}
        initialMode="login"
        onAuthenticated={() => navigate('/my-account')}
      />
      <AddPropertyModal isOpen={showListProperty} onClose={() => setShowListProperty(false)} />
      <QuickLandLeadModal isOpen={showQuickLand} onClose={() => setShowQuickLand(false)} onFullForm={() => setShowListProperty(true)} />
    </header>
  );
};

// Hover dropdown used by desktop nav groups (Properties / Explore / Join & Contact).
// Kept in-file to avoid another top-level import for a 30-line helper.
const DesktopDropdown = ({ label, testId, onClickLabel, children }) => (
  <div className="relative group">
    <button
      onClick={onClickLabel}
      className="am-nav-link"
      data-testid={testId}
    >
      {label}
      <ChevronDown size={12} className="am-nav-chevron text-slate-400" />
    </button>
    <div className="absolute left-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
      <div className="w-56 bg-white rounded-xl shadow-xl border border-slate-200 py-1 overflow-hidden">
        {children}
      </div>
    </div>
  </div>
);
